/** COCO-17 bone pairs (matches pose-core). */
export const COCO_BONES = [
  [0, 1],
  [0, 2],
  [1, 3],
  [2, 4],
  [5, 7],
  [7, 9],
  [6, 8],
  [8, 10],
  [5, 6],
  [5, 11],
  [6, 12],
  [11, 12],
  [11, 13],
  [13, 15],
  [12, 14],
  [14, 16],
];

export const COCO = {
  LShoulder: 5,
  RShoulder: 6,
  LElbow: 7,
  RElbow: 8,
  LWrist: 9,
  RWrist: 10,
  LHip: 11,
  RHip: 12,
  LKnee: 13,
  RKnee: 14,
  LAnkle: 15,
  RAnkle: 16,
};

/** MediaPipe Hands 21 bone pairs. */
export const HAND_BONES = [
  [0, 1],
  [1, 2],
  [2, 3],
  [3, 4],
  [0, 5],
  [5, 6],
  [6, 7],
  [7, 8],
  [0, 9],
  [9, 10],
  [10, 11],
  [11, 12],
  [0, 13],
  [13, 14],
  [14, 15],
  [15, 16],
  [0, 17],
  [17, 18],
  [18, 19],
  [19, 20],
  [5, 9],
  [9, 13],
  [13, 17],
];

export const INDEX_TIP = 8;

export function samplePlayerHands(sample, player = "left") {
  const side =
    sample?.[player] ||
    (player === "both" ? sample?.left || sample?.right : null) ||
    sample?.left ||
    sample?.right;
  const hands = side?.hands;
  if (!hands || hands.layout !== "mediapipe_hands21") return null;
  return hands;
}

/**
 * API meters → Three.js Y-up.
 * - relative / mono_world: MediaPipe world is person-right = +X and +Y toward feet
 *   in our export → negate X (match camera/video L/R / racket hand) and Y (upright).
 * - table / device: Quest mapping (x, z, y) so table sits on XZ
 */
export function apiJointToThree(j, metric = "relative") {
  if (!j) return [0, 0, 0];
  const x = Number(j.x) || 0;
  const y = Number(j.y) || 0;
  const z = Number(j.z) || 0;
  if (metric === "table" || metric === "device") {
    return [x, z, y];
  }
  // Mirror X so anatomical L/R matches the video (facing-camera view).
  return [-x, -y, z];
}

const ANKLE_IDX = [COCO.LAnkle, COCO.RAnkle];

/**
 * Y translation so the lowest ankle (or joint) sits on the floor plane y=0.
 * Mono/world samples are hip-centered, so without this the floor cuts through the hips.
 */
export function avatarGroundOffsetY(
  joints,
  metric = "relative",
  scoreThreshold = 0.2
) {
  if (!Array.isArray(joints) || !joints.length) return 0;
  let minY = Infinity;
  const consider = (j) => {
    if (!j || Number(j.score || 0) < scoreThreshold) return;
    const y = apiJointToThree(j, metric)[1];
    if (Number.isFinite(y) && y < minY) minY = y;
  };
  for (const i of ANKLE_IDX) consider(joints[i]);
  if (!Number.isFinite(minY)) {
    for (const j of joints) consider(j);
  }
  if (!Number.isFinite(minY)) return 0;
  return -minY;
}

export function samplePlayerJoints(sample, player = "left") {
  const side =
    sample?.[player] ||
    (player === "both" ? sample?.left || sample?.right : null) ||
    sample?.left ||
    sample?.right;
  const joints = side?.joints;
  if (!Array.isArray(joints) || joints.length < 17) return null;
  return joints;
}

export function findSampleIndex(samples, tMs) {
  if (!samples?.length) return 0;
  let lo = 0;
  let hi = samples.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if ((samples[mid].t_ms || 0) < tMs) lo = mid + 1;
    else hi = mid;
  }
  if (
    lo > 0 &&
    Math.abs((samples[lo - 1].t_ms || 0) - tMs) <
      Math.abs((samples[lo].t_ms || 0) - tMs)
  ) {
    return lo - 1;
  }
  return lo;
}

/**
 * Absolute video t_ms of the first pose3d sample (segment In / late detect).
 * Viewer playback is relative to this so scrub starts at motion, not empty lead-in.
 */
export function pose3dTimelineOriginMs(samples) {
  if (!samples?.length) return 0;
  const t0 = Number(samples[0]?.t_ms);
  return Number.isFinite(t0) && t0 > 0 ? t0 : 0;
}

export function pose3dTimelineDurationMs(samples) {
  if (!samples?.length) return 0;
  const t0 = pose3dTimelineOriginMs(samples);
  const last = Number(samples[samples.length - 1]?.t_ms) || 0;
  return Math.max(0, last - t0);
}

function fpsClose(a, b) {
  const na = Number(a);
  const nb = Number(b);
  if (!Number.isFinite(na) || !Number.isFinite(nb) || na <= 0 || nb <= 0) {
    return true;
  }
  return Math.abs(na - nb) < 0.05;
}

/** Whether a cached pose3d timeline matches the coach In/Out segment (+ optional fps). */
export function pose3dMatchesSegment(
  pose3d,
  startFrame,
  endFrame,
  poseResult = null,
  poseFps = null
) {
  if (!pose3d?.samples?.length) return false;
  const wantStart = startFrame != null ? Number(startFrame) || 0 : 0;
  const wantEnd =
    endFrame == null || endFrame === "" || Number(endFrame) < 0
      ? null
      : Number(endFrame);

  const settings = pose3d.settings || {};
  if (poseFps != null) {
    const got = settings.pose_fps ?? pose3d.fps;
    if (!fpsClose(poseFps, got)) return false;
  }

  const hasSf =
    settings.start_frame != null || settings.pose_start_frame != null;
  if (hasSf) {
    const rs = Number(settings.start_frame ?? settings.pose_start_frame) || 0;
    const reRaw = settings.end_frame ?? settings.pose_end_frame;
    const re =
      reRaw == null || reRaw === "" || Number(reRaw) < 0
        ? null
        : Number(reRaw);
    return wantStart === rs && wantEnd === re;
  }

  // Older artifacts: require same job_id and matching 2D pose range.
  if (!poseResult?.settings) return wantStart === 0 && wantEnd == null;
  const jobOk =
    !pose3d.job_id ||
    !poseResult.job_id ||
    String(pose3d.job_id) ===
      String(poseResult.job_id || poseResult.video_hash || "");
  if (!jobOk) return false;
  const rs = Number(poseResult.settings.start_frame) || 0;
  const reRaw = poseResult.settings.end_frame;
  const re =
    reRaw == null || reRaw === "" || Number(reRaw) < 0
      ? null
      : Number(reRaw);
  return wantStart === rs && wantEnd === re;
}
